// Implements: F-GEN-033, F-GEN-034, F-UIX-025, F-UIX-008, F-UIX-009, F-PRV-017
/**
 * One diagram of one version, as the v3 `diagramPane` draws it. Pure presentation.
 *
 * - `ready`: the stored diagram DRAWN in the application's one sandboxed frame (the frame route
 *   with `?artifact=diagram` or `?artifact=diagram_walkthrough`), and a "Volver a dibujar" that is
 *   always off, with the sentence that says why (a version is drawn once), and before it the
 *   v3 "Descargar (ZIP)" of the version's diagram package and, for the journey only (P6-4: the
 *   walkthrough is not shared), "Compartir". The journey carries its
 *   legend (only the keys the drawing really shows, owner 2026-10-19 6A: "Pantalla" and "Flecha"
 *   always; "Borde de color: pantalla de inicio" and "— — Camino alternativo" only when the
 *   container found those marks in the source the frame posted), the walkthrough its own sentence.
 * - `drawing`: a status saying it is being drawn and that the panel may be closed.
 * - `none`: what this diagram is, the brief it is drawn from (P6-2, prefilled with the mockup's
 *   brief: the server refuses a blank one), and the draw button. An older version is shown
 *   read-only (P6-1): it says so and offers no draw.
 *
 * A refusal is shown above the draw with its own sentence (`DG_REFUSAL`); it switches the draw
 * off, because pressing again cannot change it. `journey_required` also offers a way to the flow
 * tab. Nothing here formats money: the drawn line's cost comes as text from the container, for an
 * editor only (P7-1), and its paragraph is marked `data-version-cost` like every version cost.
 */

import type { ReactElement, ReactNode, Ref } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { Icon } from "../../../shared/ui/atoms/Icon";
import { SandboxedFrame } from "../../../shared/ui/atoms/SandboxedFrame";
import { Textarea } from "../../../shared/ui/atoms/Textarea";
import { Field } from "../../../shared/ui/molecules/Field";
import type { FrameDocumentPath } from "../../../shared/ui/frameDocumentPath";
import type { DiagramKind, DiagramRefusalReason } from "../../../types/diagrams";
import type { DiagramPaneState } from "../model/diagramState";
import { NO_MARKS } from "../model/legendKeys";
import type { LegendKeys } from "../model/legendKeys";
import type { DrawnWhen } from "../model/drawnWhen";
import styles from "./DiagramPane.module.css";

export interface DiagramPaneProps {
  readonly kind: DiagramKind;
  readonly state: DiagramPaneState;
  /** The number of the version shown (`v7`). */
  readonly versionNumber: number;
  /** The frame of the stored diagram, used when `ready`. */
  readonly frameSrc: FrameDocumentPath;
  /** `false` for an older version: it shows what it holds and draws nothing (P6-1). */
  readonly drawable: boolean;
  readonly brief: string;
  readonly onBriefChange: (value: string) => void;
  readonly onDraw: () => void;
  /** A call is in flight (the quote or the draw): the form is locked. */
  readonly busy?: boolean | undefined;
  /** The draw is held back for a while (a rate limit): the button is off. */
  readonly paused?: boolean | undefined;
  readonly refusal?: DiagramRefusalReason | null | undefined;
  /**
   * The sentence of a usage refusal (a `403 USAGE_LIMIT_REACHED`: the monthly cap, v3
   * `cap_reached`), shown as the same "No se dibujó." notice; it switches the draw off too.
   */
  readonly usageRefusal?: string | null | undefined;
  readonly onGoToJourney?: (() => void) | undefined;
  /** Announce the diagram as just drawn (a run that just finished). */
  readonly announce?: boolean | undefined;
  /**
   * When the stored diagram was drawn and what it cost, for whoever may edit the mockup (P7-1);
   * `null` for anybody else, or while the costs are read: the line then names only the version.
   */
  readonly drawn?: DrawnDiagram | null | undefined;
  /** Downloads the version's diagram package (v3 "Descargar (ZIP)"); no button without it. */
  readonly onDownload?: (() => void) | undefined;
  /** A download is in flight: its button is off. */
  readonly downloading?: boolean | undefined;
  /** Opens the share control on this diagram (v3 "Compartir"); the journey only (P6-4). */
  readonly onShare?: (() => void) | undefined;
  /** The marks the drawn journey uses (owner 6A); none until its source is known. */
  readonly legend?: LegendKeys | undefined;
  /** The drawn frame, for the listener that reads the source it posts (owner 6A). */
  readonly frameRef?: Ref<HTMLIFrameElement> | undefined;
  /** What the container adds under the pane: a wait, an error. */
  readonly children?: ReactNode;
}

/** The drawn line of an editor: when (`drawnWhen`) and the cost as text ("USD 0.02"). */
export interface DrawnDiagram {
  readonly when: DrawnWhen;
  readonly cost: string;
}

function drawnLine(t: ReturnType<typeof useTranslation>["t"], n: string, drawn: DrawnDiagram | null): string {
  if (drawn === null) return t("diagram.drawn.help", { n });
  return drawn.when.kind === "today"
    ? t("diagram.drawn.helpToday", { n, time: drawn.when.time, cost: drawn.cost })
    : t("diagram.drawn.helpDay", { n, day: drawn.when.day, cost: drawn.cost });
}

const LOWER = { journey: "diagram.lower.journey", walkthrough: "diagram.lower.walkthrough" } as const;
const BODY = { journey: "diagram.empty.body.journey", walkthrough: "diagram.empty.body.walkthrough" } as const;
const FRAME_TITLE = { journey: "diagram.frame.title", walkthrough: "diagram.frame.walkthrough" } as const;
const REFUSAL_KEY = {
  no_version: "diagram.refusal.noVersion",
  diagram_already_drawn: "diagram.refusal.diagramAlreadyDrawn",
  diagram_in_progress: "diagram.refusal.diagramInProgress",
  journey_required: "diagram.refusal.journeyRequired",
} as const;

export const DIAGRAM_BRIEF_ID = "diagram-brief";

export function DiagramPane({
  kind,
  state,
  versionNumber,
  frameSrc,
  drawable,
  brief,
  onBriefChange,
  onDraw,
  busy = false,
  paused = false,
  refusal = null,
  usageRefusal = null,
  onGoToJourney,
  announce = false,
  drawn = null,
  onDownload,
  downloading = false,
  onShare,
  legend = NO_MARKS,
  frameRef,
  children,
}: DiagramPaneProps): ReactElement {
  const { t } = useTranslation();
  const lower = t(LOWER[kind]);
  const n = String(versionNumber);
  const whyId = `diagram-why-${kind}`;

  if (state === "ready") {
    return (
      <div className={styles.pane}>
        {announce ? <p role="status">{t("diagram.result.ready")}</p> : null}
        <SandboxedFrame
          key={frameSrc}
          className={styles.frame}
          title={t(FRAME_TITLE[kind])}
          src={frameSrc}
          frameRef={frameRef}
        />
        {kind === "walkthrough" ? (
          <p className={styles.legend}>{t("diagram.legend.walkthrough")}</p>
        ) : (
          <ul className={styles.legendList} aria-label={t("diagram.legend.label")}>
            <li>
              <Icon name="circle" size="sm" />
              {t("diagram.legend.screen")}
            </li>
            {legend.start ? <li className={styles.legendStart}>{t("diagram.legend.start")}</li> : null}
            <li>{t("diagram.legend.action")}</li>
            {legend.alternative ? <li>{t("diagram.legend.alternative")}</li> : null}
          </ul>
        )}
        <div className={styles.bar}>
          {onDownload === undefined ? null : (
            <Button variant="secondary" onClick={onDownload} disabled={downloading}>
              <Icon name="download" size="sm" />
              {t("diagram.drawn.download")}
            </Button>
          )}
          {onShare === undefined || kind !== "journey" ? null : (
            <Button variant="ghost" onClick={onShare}>
              <Icon name="link" size="sm" />
              {t("diagram.drawn.share")}
            </Button>
          )}
          <span className={styles.spacer} />
          <Button variant="secondary" disabled describedBy={whyId}>
            <Icon name="refresh" size="sm" />
            {t("diagram.drawn.redraw")}
          </Button>
        </div>
        <p className={styles.help} id={whyId} data-version-cost={drawn === null ? undefined : ""}>
          {drawnLine(t, n, drawn)} {t("diagram.drawn.redrawDisabled")}
        </p>
        {children}
      </div>
    );
  }

  if (state === "drawing") {
    return (
      <div className={styles.pane}>
        <div className={styles.state} role="status">
          <Icon name="loader" size="sm" />
          <h3 className={styles.title}>{t("diagram.drawing.title", { diagram: lower, n })}</h3>
          <p className={styles.body}>{t("diagram.drawing.body")}</p>
        </div>
        {children}
      </div>
    );
  }

  const empty = brief.trim() === "";
  const refused = refusal !== null || usageRefusal !== null;
  const reason =
    refusal === null
      ? usageRefusal
      : refusal === "diagram_in_progress"
        ? t(REFUSAL_KEY[refusal], { n })
        : t(REFUSAL_KEY[refusal]);
  return (
    <div className={styles.pane}>
      <div className={styles.state}>
        <Icon name="diagram" size="sm" />
        <h3 className={styles.title}>{t("diagram.empty.title", { n, diagram: lower })}</h3>
        <p className={styles.body}>{t(BODY[kind])}</p>
      </div>
      {!refused ? null : (
        <div className={styles.notice} role="alert">
          <Icon name="alert-circle" size="sm" />
          <div>
            <p>
              <b>{t("diagram.refusal.title")}</b> {reason}
            </p>
            {refusal === "journey_required" && onGoToJourney !== undefined ? (
              <Button variant="ghost" size="sm" onClick={onGoToJourney}>
                {t("diagram.refusal.toJourney")}
              </Button>
            ) : null}
          </div>
        </div>
      )}
      {drawable ? (
        <form
          className={styles.state}
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            if (!busy && !paused && !empty && !refused) onDraw();
          }}
        >
          <Field id={DIAGRAM_BRIEF_ID} label={t("diagram.brief.label")}>
            <Textarea id={DIAGRAM_BRIEF_ID} value={brief} onChange={onBriefChange} disabled={busy} rows={3} />
          </Field>
          <Button variant="primary" type="submit" disabled={busy || paused || empty || refused}>
            <Icon name="diagram" size="sm" />
            {t("diagram.empty.draw", { diagram: lower })}
          </Button>
          <p className={styles.help}>{t("diagram.empty.help", { n })}</p>
        </form>
      ) : (
        <p className={styles.help}>{t("diagram.version.readOnly")}</p>
      )}
      {children}
    </div>
  );
}
